/**
 * KanbanLane — a single column.
 *
 * Trello-essentials supported here:
 *   - inline rename (click the title → input)
 *   - lane menu (… → Rename / Archive / Sort)
 *   - footer "+ Add a card" composer (when `onCardCreate` is wired)
 *   - drag-drop target (HTML5 native)
 *   - WIP-limit badge + over-limit warning strip
 *
 * Line-limit justification (rule 05_agent_operating_system.mdc §1.7):
 *   This file > 300 lines because it owns the lane chrome (header,
 *   menu, color picker, description sub-line, WIP strip, drop hint,
 *   triage tabs, bulk-action bar) AND the lane's full drop-target
 *   logic (two MIMEs, collapsed branch). Each concern was already
 *   extracted where it was extractable (AddCardComposer, KanbanCard,
 *   CardAiBand, TriageBulkBar, useTriageTabUrl). Further extraction
 *   would invert the I→O relationship — the render branches share
 *   local state (renaming, menuOpen, dragOver, triageTab) and the
 *   same drop handler — so a deeper split would add prop-drilling
 *   noise without isolation gains. Module D bulk/url-sync hooks
 *   added ~80 lines, balanced by extracting TriageBulkBar (154 LOC)
 *   and useTriageTabUrl (137 LOC) into siblings.
 */

import { memo, useEffect } from 'react'
import { emitCardSurfaceTelemetry } from '../card-surface/telemetry'
import { useOptionalCardSurfaceRuntime } from '../card-surface/runtime/useCardCapabilityRuntime'
import { MULTI_CARD_MIME, parseDragCardIds } from '../utils/cardSelection'
import {
  computeDropAdoptPriority,
  computeDropIndexFromMidpoints,
  computeReorderPosition,
} from '../utils/cardReorder'
import { KANBAN_ALLOCATE_EMAIL_MIME } from './KanbanEmailAllocationRail'
import { KANBAN_CARD_MIME, KANBAN_PLACEMENT_MIME } from './lane/constants'
import { useKanbanLane } from './lane/useKanbanLane'
import { LaneCollapsed } from './lane/LaneCollapsed'
import { LaneHeader } from './lane/LaneHeader'
import { LaneBody } from './lane/LaneBody'
import type { KanbanLaneProps } from './lane/types'

export type { KanbanLaneProps } from './lane/types'

export const KanbanLane = memo(function KanbanLane(props: KanbanLaneProps) {
  const vm = useKanbanLane(props)
  const { collapsed, lane, cards, onCardDrop, onLaneContextMenu, onLaneEmailAllocate } = props
  const { dragOver, setDragOver, updateDropLine, clearDropLine } = vm
  const runtime = useOptionalCardSurfaceRuntime()
  const surfaceId =
    props.cardSurfaceId ??
    (lane.display_mode === 'checklist' ? 'checklist-row' : 'board-lane-card')
  const rolloutMode = props.cardSurfaceRolloutMode ?? 'legacy'
  const capabilityCount = runtime
    ? Object.values(runtime.commands).filter(
        (command) => command.availability === 'available',
      ).length
    : 0

  // Emits once per mounted projection, not once per card. In shadow mode the
  // lane still renders legacy UI while this eligibility record makes rollback
  // cohorts observable without mounting a second, mutation-capable tile.
  useEffect(() => {
    emitCardSurfaceTelemetry({
      event: 'surface_ready',
      rolloutMode,
      runtimeMode: runtime?.mode ?? 'interactive',
      surfaceId,
      boardScoped: Boolean(lane.board),
      capabilityCount,
    })
  }, [capabilityCount, lane.board, rolloutMode, runtime?.mode, surfaceId])

  if (collapsed) return <LaneCollapsed props={props} vm={vm} />

  return (
    <div
      data-lane-id={lane.id}
      data-lane-board-id={lane.board}
      data-lane-kind={lane.kind}
      data-drag-over={dragOver || undefined}
      onContextMenu={(e) => {
        // Open the lane context menu unless the right-click happened
        // on a CARD (cards have their own onContextMenu via
        // `onCardContextMenu` and we don't want to double-fire).
        // KanbanCard renders its root with `data-card-id={card.id}`.
        if (!onLaneContextMenu) return
        const target = e.target as HTMLElement | null
        if (target?.closest?.('[data-card-id]')) return
        // Skip when the click is inside an already-open menu/popover
        // (the lane's `…` menu, colour picker, etc) — operators
        // expect right-click to dismiss those, not stack a second
        // menu on top.
        if (target?.closest?.('[role="menu"]')) return
        e.preventDefault()
        e.stopPropagation()
        onLaneContextMenu(lane, { x: e.clientX, y: e.clientY })
      }}
      onDragOver={(e) => {
        // Two MIMEs are valid lane-level drops:
        //   1. `text/x-kanban-card-id` — card move between lanes
        //      (always honoured).
        //   2. `text/x-kanban-allocate-email-id` — email row from
        //      the right rail (qf!2026-05-07). Only honoured when
        //      the host passes `onLaneEmailAllocate` so that the
        //      drop has somewhere to land. Without this branch a
        //      column with no cards (e.g. a fresh "My Inbox"
        //      board) is undroppable for emails — the original
        //      qf!2507 wiring only made KanbanCard a drop target
        //      and the operator UX broke down to "I can't drop
        //      anything here" (operator complaint 2026-05-07).
        const types = e.dataTransfer.types
        const isCard = types.includes(KANBAN_CARD_MIME)
        const isEmail =
          !!onLaneEmailAllocate && types.includes(KANBAN_ALLOCATE_EMAIL_MIME)
        if (!isCard && !isEmail) return
        e.preventDefault()
        // ARC-10 MR3 — Alt-drag a card = LINK it into this lane (extra
        // live presence) instead of moving. The 'link' cursor telegraphs
        // the semantics before the drop.
        e.dataTransfer.dropEffect = isEmail || (isCard && e.altKey) ? 'link' : 'move'
        if (!dragOver) setDragOver(true)
        // FEAT-2026-07-03-KANBAN-DROP-LINE — drive the live insertion line for a
        // single-card reorder drag. A multi-card drag keeps the legacy append
        // (no single insertion point), so we clear the line for it; email drags
        // clear it too (they create a new card, not a reorder).
        if (isCard && !types.includes(MULTI_CARD_MIME)) {
          updateDropLine(e.currentTarget, e.clientY)
        } else {
          clearDropLine()
        }
      }}
      onDragLeave={(e) => {
        // Filter out child enter events — without this, dragLeave fires
        // every time the cursor crosses a card boundary inside the lane,
        // which produces a strobing border.
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
        setDragOver(false)
        clearDropLine()
      }}
      onDrop={(e) => {
        const types = e.dataTransfer.types
        // Email-allocate path — create a new card in this lane with
        // the email pre-linked. Skipped when the host hasn't wired
        // the handler (the dragOver guard above keeps us out of
        // here in that case, but defend anyway).
        if (
          onLaneEmailAllocate &&
          types.includes(KANBAN_ALLOCATE_EMAIL_MIME)
        ) {
          const emailId = e.dataTransfer.getData(KANBAN_ALLOCATE_EMAIL_MIME)
          if (!emailId) return
          e.preventDefault()
          setDragOver(false)
          clearDropLine()
          const suggested = e.dataTransfer.getData('text/plain') || undefined
          void onLaneEmailAllocate(lane, emailId, suggested)
          return
        }
        // Card-move path (existing behaviour). Reads the multi-select id
        // list (when present) so dropping a multi-selection moves the whole
        // batch across lanes, not just the grabbed card.
        const cardId = e.dataTransfer.getData(KANBAN_CARD_MIME)
        if (!cardId) return
        e.preventDefault()
        setDragOver(false)
        clearDropLine()
        const ids = parseDragCardIds(e.dataTransfer.getData(MULTI_CARD_MIME), cardId)
        if (!onCardDrop) return
        // FEAT-2026-07-02-KANBAN-DRAG-REORDER — translate the pointer drop into
        // a float `position` so the card lands WHERE it was dropped (same-lane
        // reorder + cross-lane insert at index), not just appended. We measure
        // the lane's own card tiles (each carries `data-card-id`) against the
        // cursor Y, then compute the midpoint position between the new
        // neighbours from THIS lane's card list (which carries the stored
        // positions). Multi-drops keep the legacy append (no single insertion
        // point); a lane with no measurable tiles yields `position = undefined`
        // and the move falls back to append.
        let position: number | undefined
        let adoptPriority: number | undefined
        if (ids.length <= 1) {
          const tiles = Array.from(
            e.currentTarget.querySelectorAll<HTMLElement>('[data-card-id]'),
          )
          if (tiles.length > 0) {
            const orderedIds = tiles
              .map((el) => el.getAttribute('data-card-id'))
              .filter((id): id is string => !!id)
            const midpoints = tiles.map((el) => {
              const r = el.getBoundingClientRect()
              return r.top + r.height / 2
            })
            const dropIndex = computeDropIndexFromMidpoints(e.clientY, midpoints)
            const byId = new Map(cards.map((c) => [c.id, c]))
            const orderedCards = orderedIds
              .map((id) => byId.get(id))
              .filter((c): c is (typeof cards)[number] => !!c)
            position = computeReorderPosition(orderedCards, dropIndex, cardId)
            // FEAT-2026-07-20-KANBAN-PRIORITY-LANE-ORDER — a cross-band drop
            // adopts the target band's priority so it sticks at the drop spot
            // instead of the priority-first comparator snapping it back. Only
            // when the band actually differs from the card's current priority.
            const band = computeDropAdoptPriority(orderedCards, dropIndex, cardId)
            const moving = byId.get(cardId)
            if (band !== null && moving && (moving.priority ?? 0) !== band) {
              adoptPriority = band
            }
          }
        }
        // ARC-10 linked cards — a placement-instance drag stamps its
        // presence id; forward it so the host moves THAT presence.
        const placementId =
          e.dataTransfer.getData(KANBAN_PLACEMENT_MIME) || undefined
        onCardDrop(
          lane,
          cardId,
          ids.length > 1 ? ids : undefined,
          position,
          placementId,
          // ARC-10 MR3 — Alt at drop time = link (add presence), not move.
          e.altKey || undefined,
          adoptPriority,
        )
      }}
      // Themed boards drive the lane bg via `--lane-bg` (set on the
      // KanbanBoardView wrapper). Non-themed boards fall back to the
      // dark zinc default. Border softened to keep the lane reading
      // as part of the canvas instead of a hard panel.
      style={{
        background: 'var(--lane-bg, transparent)',
        borderColor: 'var(--board-bg, transparent)',
      }}
      className={[
        // MOBILE-KANBAN-FIRST-PRINCIPLES-ARC §3.3 (Phase 2) —
        // mobile lane = one-per-viewport so the operator
        // horizontally swipes between lanes (Trello mobile
        // pattern). Above md the lane reverts to the legacy
        // 256px (`w-64`) inline column so desktop multi-lane
        // visibility is unchanged.
        //
        // The actual `snap-start` lives on the wrapper <div> in
        // KanbanBoardView.tsx (the direct child of the scroll
        // container) because scroll-snap only reliably targets
        // direct children of the scroll-snap-type container.
        //
        // BUG-2026-06-24-KANBAN-MOBILE-LANE-TOO-BROAD — the lane was
        // `calc(100vw-1.25rem)`: a near-full-bleed column that read as
        // edge-to-edge, so a card with a short title + a couple of meta
        // icons looked stranded in a wide empty box (operator: "lanes too
        // broad … blank space"). A phone kanban lane should read as a CARD,
        // not the whole page. So the lane is now `86vw` (a clear ~14% peek
        // of the next lane invites the swipe) and CAPPED at `22rem` (352px)
        // so on the widest phones / small foldables it never stretches into
        // a broad slab — the extra width becomes peek instead. Above md the
        // lane reverts to the legacy 256px (`w-64`) inline column so the
        // desktop multi-lane board is unchanged.
        // FIX-2026-07-27-KANBAN-CARD-PRESS-SNAPPY: scoped away from
        // `transition-all`, which animated the responsive width utilities on
        // this same element (`w-[86vw]` / `max-w-[22rem]` / `md:w-64`) — a
        // layout animation running during every drag-over.
        'group/lane relative flex h-full w-[86vw] max-w-[22rem] shrink-0 flex-col rounded-xl border transition-[transform,box-shadow,border-color,background-color] duration-150',
        'md:w-64 md:max-w-none',
        // Visual uplift (2026-05-30): a flat `shadow-sm` lifts the lane
        // off the gradient canvas so columns are easier to parse — depth
        // with ZERO density/perf cost (no backdrop-blur: that was a
        // per-lane GPU blur layer that janked scroll on long boards and
        // bought nothing but looks). Opaque bg keeps text crisp. Apply
        // the zinc fallback only when no theme is in scope; the inline
        // `style.background` override wins when a theme publishes
        // --lane-bg.
        // VISUAL UPLIFT (2026-05-31) — the base elevation lives in the
        // NON-drag branch below (a soft layered drop-shadow that lets the
        // lane float on the new accent-glow canvas) so it never co-exists
        // with the drag-state `shadow-lg`. Two box-shadow utilities on one
        // element race on CSS source-order; keeping exactly one per state
        // makes the depth deterministic.
        'bg-neutral-50 dark:bg-zinc-900/80',
        // BUG-2026-05-17-KANBAN-THEMED-LANE-TITLE-INVISIBLE — set a
        // base text color on the wrapper so descendants using
        // `color: var(--lane-fg, currentColor)` fall back to a
        // readable colour when no theme is in scope. Without this,
        // currentColor cascades up to wherever `text-*` is last
        // declared (often the page body), and JOB-auto-themed boards
        // ended up with `text-neutral-800` titles drawn against a
        // `lane_bg: #1e2538` (indigo preset) → invisible.
        // KanbanCard already opts in via `var(--card-fg)` since
        // qf!2926; this is the parity fix for the lane shell.
        'text-neutral-800 dark:text-neutral-100',
        dragOver
          ? 'border-blue-500 dark:border-blue-400 bg-blue-50/60 dark:bg-blue-950/40 shadow-lg ring-2 ring-blue-400/30 scale-[1.005]'
          : 'border-neutral-200/70 dark:border-white/10 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_14px_30px_-20px_rgba(16,24,40,0.5)]',
      ].join(' ')}
    >
      {/* Visible drop hint that overlays the lane while a card is in
          flight. Rendered behind the card list so it doesn't capture
          pointer events and break the drop. */}
      {dragOver && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-2 rounded border-2 border-dashed border-blue-400/70 dark:border-blue-500/70"
        />
      )}
      <LaneHeader props={props} vm={vm} />
      <LaneBody props={props} vm={vm} />
    </div>
  )
})
